import React from 'react';
import { Link } from 'react-router-dom';

/**
 * Program card — warm paper surface, subtle hover lift (theme.css .oli-card).
 * Used across the Programs hub, Home, and anywhere a program/initiative is
 * summarized.
 *
 * Props:
 *   eyebrow  — small uppercase label above the title (e.g. 'Program')
 *   title    — card heading
 *   children — body copy
 *   image    — optional image src rendered at the top
 *   imageAlt — alt text for the image
 *   to       — optional destination; renders a specific CTA link
 *   cta      — CTA label (be specific: 'Plant a Patriotic Garden', never 'Learn More')
 *   accent   — 'green' | 'gold' | 'red' top rule color
 */
export const ProgramCard = ({
  eyebrow,
  title,
  children,
  image,
  imageAlt = '',
  to,
  href,
  cta,
  accent = 'green',
}) => {
  const accentBar = {
    green: 'bg-forest',
    gold: 'bg-gold',
    red: 'bg-flag_red',
  }[accent] || 'bg-forest';

  return (
    <article className="oli-card oli-paper border border-line overflow-hidden flex flex-col h-full">
      <div className={`h-1.5 ${accentBar}`} aria-hidden="true" />
      {image && (
        <img src={image} alt={imageAlt} className="w-full h-48 object-cover" loading="lazy" />
      )}
      <div className="p-6 sm:p-7 flex flex-col flex-grow">
        {eyebrow && <p className="oli-label text-gold mb-2">{eyebrow}</p>}
        <h3 className="font-display font-bold text-xl sm:text-2xl text-forest leading-snug">{title}</h3>
        <div className="text-ink_soft text-base leading-relaxed mt-3 flex-grow">{children}</div>
        {(to || href) && cta && (
          href ? (
            <a
              href={href}
              className="inline-flex items-center gap-1.5 mt-5 font-bold text-forest hover:text-gold transition-colors duration-200 text-sm uppercase tracking-label"
            >
              {cta}
              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
              </svg>
            </a>
          ) : (
            <Link
              to={to}
              className="inline-flex items-center gap-1.5 mt-5 font-bold text-forest hover:text-gold transition-colors duration-200 text-sm uppercase tracking-label"
            >
              {cta}
              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
              </svg>
            </Link>
          )
        )}
      </div>
    </article>
  );
};

export default ProgramCard;
